import React, {useEffect, useRef, useState} from "react";
import {ReactComponent as CloseIcon} from "./svg/close.svg";
import './scss/UIElements.scss';
import {ReactComponent as SnackError} from "./svg/snack-error.svg";
import {ReactComponent as SnackWarning} from "./svg/snack-warning.svg";
import {ReactComponent as SnackInfo} from "./svg/snack-info.svg";
import {ReactComponent as SnackSuccess} from "./svg/snack-success.svg";
import {ReactComponent as HelpIcon} from "./svg/help.svg";
import {useSnackbar} from "./Content";
import {useIntl} from "react-intl";

export function Snackbar({show, text, state, autoHide}) {
    const {setSnackbarData} = useSnackbar()
    const icon = state === "error" ? <SnackError /> :
        state === "warning" ? <SnackWarning /> :
            state === "success" ? <SnackSuccess /> :
                <SnackInfo />

    const hideSnackbar = <button className={"hide-snackbar"}
                                 onClick={e=> {
                                     e.stopPropagation()
                                     setSnackbarData(state => ({
                                         ...state,
                                         show: false
                                     }))
                                 }}>
        <CloseIcon />
    </button>

    useEffect(()=> {
        let timeOutAutoHide = setTimeout(()=> {
            if (autoHide) {
                setSnackbarData(state => ({
                    ...state,
                    show: false
                }))
            }
        }, 5000)

        return () => {clearTimeout(timeOutAutoHide)}
    },[show, text, state, autoHide])

    return <>
        {show &&
        <div className={"snackbar"} data-state={state}>
            {icon}
            <p>{text}</p>
            {hideSnackbar}
        </div>
        }
    </>
}

export function Skeleton({height, width, circle, inline, marginBottom, classText} ) {
    let className = "skeleton"
    if (circle) {className = className.concat(' circle')}
    if (classText) {className = className.concat(' '+classText)}
    let display = inline ? "inline-block" : "block"
    return <div
        className={className}
        style={{height: height, width: width, display: display, marginBottom: marginBottom}}
    />
}

export function NestedDropdown({ placeholder, children }) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef();

    const childrenWithSelections = React.Children.toArray(children).filter(
        child => child.props.selectedValues?.length > 0
    )
    const selectedCount = childrenWithSelections.length

    const getDisplayText = () => {
        if (selectedCount === 0) return placeholder
        if (selectedCount === 1) {
            // Get the child that has selections
            const selectedChild = childrenWithSelections[0];

            // Get all selected labels for this child
            const selectedLabels = selectedChild.props.selectedValues.map(value =>
                selectedChild.props.options.find(opt => opt.value === value)?.label
            ).filter(Boolean); // Filter out any undefined values

            return selectedLabels.join(", ")
        }
        return `${placeholder} (${selectedCount})`
    };

    const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
    };

    return (
        <div className={`multi-select ${selectedCount > 0 ? "active" : ""}`} ref={dropdownRef}>
            <div className="select" onClick={toggleDropdown}>
                {getDisplayText()}
            </div>
            {isOpen && (
                <div className="dropdown nested-dropdown">
                    {children}
                </div>
            )}
        </div>
    );
}

export function MultiSelect({ options, selectedValues, onSelectChange, placeholder }) {
    const intl = useIntl()
    const [filter, setFilter] = useState("")
    const [filteredOptions, setFilteredOptions] = useState(options)
    const [isOpen, setIsOpen] = useState(false)
    const multiselectRef = useRef()

    const labeledValues = selectedValues.map(value => options.find(obj => obj?.value === value)?.label)

    useEffect(() => {
        setFilteredOptions(
            options.filter((option) =>
                option.label.toLowerCase().includes(filter.toLowerCase())
            )
        )
    }, [filter, options, selectedValues])

    const handleSelectChange = (value) => {
        const updatedValues = selectedValues.includes(value)
            ? selectedValues.filter((v) => v !== value)
            : [...selectedValues, value]

        onSelectChange(updatedValues)
    }

    const handleClickOutside = (event) => {
        if (multiselectRef.current && !multiselectRef.current.contains(event.target)) {
            setIsOpen(false)
        }
    }

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside)
        return () => {
            document.removeEventListener("mousedown", handleClickOutside)
        }
    }, [])

    const toggleDropdown = () => {
        setIsOpen(!isOpen)
    }

    const selected = selectedValues.length > 0

    return (
        <div className={`multi-select ${selected ? "active" : ""}`} ref={multiselectRef}>
            <div className={"select"} onClick={toggleDropdown}>
                {labeledValues.length > 0
                    ? labeledValues.join(", ")
                    : placeholder}
            </div>
            {isOpen && (
                <div className="dropdown">
                    <input
                        type="text"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        placeholder={intl.formatMessage({id:'filterOptions', defaultMessage:'Filter options'})}
                        autoFocus
                    />
                    <ul>
                        {filteredOptions?.length < 1 &&
                            <li><em>{intl.formatMessage({id:'noOptionsAvailable', defaultMessage:'No options available'})}</em></li>
                        }
                        {filteredOptions.map((option) => (
                            <li key={option.value} title={option.title}>
                                <label>
                                    <input
                                        type="checkbox"
                                        value={option.value}
                                        checked={selectedValues.includes(option.value)}
                                        onChange={() => handleSelectChange(option.value)}
                                    />
                                    {option.label}
                                </label>
                            </li>
                        ))}
                    </ul>
                    <button onClick={() => onSelectChange([])}>{intl.formatMessage({id:'clearSelections', defaultMessage:'Clear Selections'})}</button>
                </div>
            )}
        </div>
    );
}

export function Tooltip({ text }) {
    const [showTooltip, setShowTooltip] = useState(false)
    return <div className={"tooltip-icon"}
                aria-label={"Tooltip: " + text}
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
    >
        <HelpIcon />
        {showTooltip &&
        <div className={"tooltip"}>
            { text }
        </div>
        }
    </div>
}

export function HoverTooltip({ className, children, text }) {
    const [showTooltip, setShowTooltip] = useState(false)
    return <div className={`${className} tooltip-container`}
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
    >
        {children}
        {showTooltip &&
        <div className={"tooltip"}>
            { text }
        </div>
        }
    </div>
}